import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { vocabularyService } from '../services/vocabulary';
import { VocabularyCard, VocabularyCardCreate, SRSStats } from '../types';

const SRS: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<SRSStats | null>(null);
  const [cards, setCards] = useState<VocabularyCard[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [englishWord, setEnglishWord] = useState('');
  const [czechTranslation, setCzechTranslation] = useState('');
  const [exampleSentence, setExampleSentence] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [category, setCategory] = useState('');
  const [difficulty, setDifficulty] = useState('A2');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, cardsData] = await Promise.all([
        vocabularyService.getStats(),
        vocabularyService.getAllCards()
      ]);
      setStats(statsData);
      setCards(cardsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const newCard: VocabularyCardCreate = {
        english_word: englishWord,
        czech_translation: czechTranslation,
        example_sentence: exampleSentence || undefined,
        pronunciation: pronunciation || undefined,
        category: category || undefined,
        difficulty_level: difficulty
      };
      
      await vocabularyService.createCard(newCard);
      
      // Reset form
      setEnglishWord('');
      setCzechTranslation('');
      setExampleSentence('');
      setPronunciation('');
      setCategory('');
      setDifficulty('A2');
      setShowAddForm(false);
      
      // Reload data
      await loadData();
      alert('Card added successfully! 🎉');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error adding card');
    }
  };

  const handleDeleteCard = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        await vocabularyService.deleteCard(id);
        await loadData();
      } catch (error) {
        console.error('Error deleting card:', error);
      }
    }
  };

  const addStarterPack = async () => {
    if (!window.confirm('Add 20 basic vocabulary cards to get started?')) return;
    
    const starterCards: VocabularyCardCreate[] = [
      { english_word: 'hello', czech_translation: 'ahoj', difficulty_level: 'A1', category: 'greetings' },
      { english_word: 'goodbye', czech_translation: 'nashledanou', difficulty_level: 'A1', category: 'greetings' },
      { english_word: 'please', czech_translation: 'prosím', difficulty_level: 'A1', category: 'basics' },
      { english_word: 'thank you', czech_translation: 'děkuji', difficulty_level: 'A1', category: 'basics' },
      { english_word: 'yes', czech_translation: 'ano', difficulty_level: 'A1', category: 'basics' },
      { english_word: 'no', czech_translation: 'ne', difficulty_level: 'A1', category: 'basics' },
      { english_word: 'water', czech_translation: 'voda', difficulty_level: 'A1', category: 'food' },
      { english_word: 'food', czech_translation: 'jídlo', difficulty_level: 'A1', category: 'food' },
      { english_word: 'house', czech_translation: 'dům', difficulty_level: 'A1', category: 'places' },
      { english_word: 'friend', czech_translation: 'přítel', difficulty_level: 'A1', category: 'people' },
      { english_word: 'book', czech_translation: 'kniha', difficulty_level: 'A1', category: 'objects' },
      { english_word: 'time', czech_translation: 'čas', difficulty_level: 'A1', category: 'basics' },
      { english_word: 'day', czech_translation: 'den', difficulty_level: 'A1', category: 'time' },
      { english_word: 'night', czech_translation: 'noc', difficulty_level: 'A1', category: 'time' },
      { english_word: 'good', czech_translation: 'dobrý', difficulty_level: 'A1', category: 'adjectives' },
      { english_word: 'bad', czech_translation: 'špatný', difficulty_level: 'A1', category: 'adjectives' },
      { english_word: 'big', czech_translation: 'velký', difficulty_level: 'A1', category: 'adjectives' },
      { english_word: 'small', czech_translation: 'malý', difficulty_level: 'A1', category: 'adjectives' },
      { english_word: 'happy', czech_translation: 'šťastný', difficulty_level: 'A2', category: 'emotions' },
      { english_word: 'sad', czech_translation: 'smutný', difficulty_level: 'A2', category: 'emotions' }
    ];
    
    try {
      await vocabularyService.createBulkCards(starterCards);
      await loadData();
      alert('Starter pack added! 🎉');
    } catch (error) {
      console.error('Error adding starter pack:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-indigo-600">Vocabulary (SRS)</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-700">Points: {user?.total_points}</span>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">📚</div>
              <p className="text-gray-600">Total Cards</p>
              <p className="text-3xl font-bold text-indigo-600">{stats.total_cards}</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">⏰</div>
              <p className="text-gray-600">Due Today</p>
              <p className="text-3xl font-bold text-orange-600">{stats.cards_due_today}</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">✨</div>
              <p className="text-gray-600">Mastered</p>
              <p className="text-3xl font-bold text-green-600">{stats.cards_mastered}</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">📖</div>
              <p className="text-gray-600">Learning</p>
              <p className="text-3xl font-bold text-blue-600">{stats.cards_learning}</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">🎯</div>
              <p className="text-gray-600">Accuracy</p>
              <p className="text-3xl font-bold text-purple-600">{stats.accuracy_rate}%</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-4xl mb-2">⚡</div>
              <p className="text-gray-600">Avg. Ease</p>
              <p className="text-3xl font-bold text-pink-600">{stats.average_ease_factor}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          {stats && stats.cards_due_today > 0 && (
            <button
              onClick={() => navigate('/srs/study')}
              className="flex-1 bg-green-500 text-white py-4 rounded-lg hover:bg-green-600 text-xl font-bold shadow-lg"
            >
              🎓 Study Now ({stats.cards_due_today} cards)
            </button>
          )}
          
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-indigo-600 text-white px-6 py-4 rounded-lg hover:bg-indigo-700 shadow-lg"
          >
            ➕ Add Card
          </button>
          
          {cards.length === 0 && (
            <button
              onClick={addStarterPack}
              className="bg-purple-600 text-white px-6 py-4 rounded-lg hover:bg-purple-700 shadow-lg"
            >
              🎁 Add Starter Pack
            </button>
          )}
        </div>

        {/* Add Card Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Add New Card</h2>
            <form onSubmit={handleAddCard} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    English Word *
                  </label>
                  <input
                    type="text"
                    value={englishWord}
                    onChange={(e) => setEnglishWord(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Czech Translation *
                  </label>
                  <input
                    type="text"
                    value={czechTranslation}
                    onChange={(e) => setCzechTranslation(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Example Sentence
                </label>
                <input
                  type="text"
                  value={exampleSentence}
                  onChange={(e) => setExampleSentence(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="The cat is sleeping on the sofa."
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Pronunciation
                  </label>
                  <input
                    type="text"
                    value={pronunciation}
                    onChange={(e) => setPronunciation(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="kæt"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="food, travel, etc."
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Difficulty
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="A1">A1</option>
                    <option value="A2">A2</option>
                    <option value="B1">B1</option>
                    <option value="B2">B2</option>
                    <option value="C1">C1</option>
                    <option value="C2">C2</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
                >
                  Add Card
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Cards List */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">Your Cards ({cards.length})</h2>
          
          {cards.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No cards yet. Add some cards to start learning!
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 flex justify-between items-center"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-lg">{card.english_word}</span>
                      <span className="text-gray-600">→</span>
                      <span className="text-gray-700">{card.czech_translation}</span>
                      {card.category && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          {card.category}
                        </span>
                      )}
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {card.difficulty_level}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Seen: {card.times_seen} | Success: {
                        card.times_seen > 0 
                          ? Math.round((card.times_correct / card.times_seen) * 100) 
                          : 0
                      }% | Next: {new Date(card.next_review).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteCard(card.id)}
                    className="text-red-500 hover:text-red-700 ml-4"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SRS;
